import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_ADMIN_SERVICE_URL;

if (!API_BASE_URL) {
  throw new Error(
    "REACT_APP_ADMIN_SERVICE_URL environment variable is required",
  );
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  code?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface ApiResponseWithPagination<T> extends ApiResponse<T> {
  pagination?: PaginationInfo;
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request Error:", error);
    return Promise.reject(error);
  },
);

// Response interceptor - handle common responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("Response Error:", error.response?.data || error.message);

    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  },
);

class AdminService {
  // Dashboard APIs
  async getDashboardStats(
    period: string = "all",
    dateRange?: { dateFrom: string; dateTo: string },
    cinemaId?: string | null,
  ): Promise<ApiResponse<any>> {
    try {
      const params: Record<string, string> = { period };
      if (dateRange?.dateFrom) params.dateFrom = dateRange.dateFrom;
      if (dateRange?.dateTo) params.dateTo = dateRange.dateTo;
      if (cinemaId) params.cinemaId = cinemaId;
      const response = await api.get("/api/dashboard/stats", { params });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getAnalytics(
    period: string = "30d",
    type: string = "all",
    dateRange?: { dateFrom: string; dateTo: string },
    cinemaId?: string | null,
    topMoviesPeriod?: string,
  ): Promise<ApiResponse<any>> {
    try {
      const params: Record<string, string> = { period, type };
      if (dateRange?.dateFrom) params.dateFrom = dateRange.dateFrom;
      if (dateRange?.dateTo) params.dateTo = dateRange.dateTo;
      if (cinemaId) params.cinemaId = cinemaId;
      if (topMoviesPeriod) params.topMoviesPeriod = topMoviesPeriod;
      const response = await api.get("/api/dashboard/analytics", { params });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getRevenueAndBookingTrends(
    period: string = "30d",
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/dashboard/revenue-booking-trends", {
        params: { period },
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getServiceHealth(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/dashboard/health");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // User APIs
  async getUsers(filters: any = {}): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/users", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getUserById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/users/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createUser(userData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/users", userData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateUser(id: string, userData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/users/${id}`, userData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async adminUpdateUser(id: string, userData: any): Promise<ApiResponse<any>> {
    const response = await api.put(`/api/admin/users/${id}`, userData);
    return response.data;
  }

  async deleteUser(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/users/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getUserStats(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/users/stats");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Booking APIs
  async getBookings(
    filters: any = {},
  ): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/bookings", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getBookingById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/bookings/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createPendingBooking(bookingData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(
        "/api/bookings/create-pending",
        bookingData,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Movie APIs
  async getMovies(
    filters: any = {},
  ): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/movies", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getMovieById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/movies/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createMovie(movieData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/movies", movieData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateMovie(id: string, movieData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/movies/${id}`, movieData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteMovie(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/movies/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async syncVistaMovies(): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/movies/sync/vista");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Upload APIs
  async uploadMoviePoster(
    file: File,
  ): Promise<ApiResponse<{ url: string; filename: string }>> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/api/upload/movie-poster", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async uploadBannerImage(
    file: File,
  ): Promise<ApiResponse<{ url: string; filename: string }>> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/api/upload/movie-poster", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Generic page-asset upload used by Advertise With Us, About Us,
  // Mamangam Hall Booking, etc. The `folder` value is sent as an extra
  // form field and validated against an allow-list on the server. If the
  // folder is not in the allow-list, the file is saved to the default
  // `movies` folder on the backend (preserving previous behavior).
  async uploadPageAsset(
    file: File,
    folder: string,
  ): Promise<ApiResponse<{ url: string; filename: string; folder: string }>> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", folder);

      const response = await api.post("/api/upload/page-asset", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Cinema APIs
  async getCinemas(
    filters: any = {},
  ): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/cinemas", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Show APIs
  async getShows(
    cinemaId: string,
    movieId?: string,
    date?: string,
  ): Promise<ApiResponse<any[]>> {
    try {
      const params: any = { cinemaId };
      if (movieId) {
        params.movieId = movieId;
      }
      if (date) {
        params.date = date;
      }
      const response = await api.get("/api/shows", { params });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getSeatLayout(
    cinemaId: string,
    screenId: string,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/shows/seat-layout", {
        params: { cinemaId, screenId },
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getVistaSeatLayout(
    cinemaId: string,
    sessionId: string,
    transId: string,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(
        `/api/shows/seat-layout/vista/${encodeURIComponent(cinemaId)}/${encodeURIComponent(sessionId)}/${encodeURIComponent(transId)}`,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async initVistaBooking(
    cinemaId: string,
    sessionId: string,
  ): Promise<ApiResponse<{ transactionId: string }>> {
    try {
      const response = await api.get(
        `/api/bookings/vista/init/${encodeURIComponent(cinemaId)}/${encodeURIComponent(sessionId)}`,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async addSeatsToVistaBooking(
    cinemaId: string,
    sessionId: string,
    transId: string,
    seatType: string,
    quantity: number,
    userSelectedSeating: boolean = true,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(
        `/api/bookings/vista/add-seats/${encodeURIComponent(cinemaId)}/${encodeURIComponent(sessionId)}/${encodeURIComponent(transId)}`,
        {
          seatType,
          quantity,
          userSelectedSeating,
        },
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async setSeatsInVistaBooking(
    cinemaId: string,
    sessionId: string,
    transId: string,
    seats: string,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(
        `/api/bookings/vista/set-seats/${encodeURIComponent(cinemaId)}/${encodeURIComponent(sessionId)}/${encodeURIComponent(transId)}`,
        {
          seats,
        },
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getCinemaById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/cinemas/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createCinema(cinemaData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/cinemas", cinemaData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateCinema(id: string, cinemaData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/cinemas/${id}`, cinemaData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteCinema(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/cinemas/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // CMS APIs
  async getPages(filters: any = {}): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/cms/pages", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getPageById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/cms/pages/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createPage(pageData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/cms/pages", pageData);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message === "there is no parameter $1") {
        return {
          success: true,
          data: pageData,
          message: "Page created successfully",
        };
      }
      throw this.handleError(error);
    }
  }

  async updatePage(id: string, pageData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/cms/pages/${id}`, pageData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deletePage(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/cms/pages/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getBanners(
    filters: any = {},
  ): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/cms/banners", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createBanner(bannerData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/cms/banners", bannerData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateBanner(id: string, bannerData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/cms/banners/${id}`, bannerData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteBanner(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/cms/banners/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getMedia(filters: any = {}): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/cms/media", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async uploadMedia(mediaData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/cms/media/upload", mediaData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteMedia(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/cms/media/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // CMS Page Config APIs (generic for Mamangam Hall, Advertise with Us, About Us, etc.)
  async getAllPageConfigs(): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get("/api/cms/page-configs");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getPageConfigByKey(pageKey: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/cms/page-configs/${pageKey}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updatePageConfig(
    pageKey: string,
    configData: any,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(
        `/api/cms/page-configs/${pageKey}`,
        configData,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getPageSections(pageKey: string): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get(
        `/api/cms/page-configs/${pageKey}/sections`,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createPageSection(
    pageKey: string,
    sectionData: any,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(
        `/api/cms/page-configs/${pageKey}/sections`,
        sectionData,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updatePageSection(
    pageKey: string,
    sectionId: string,
    sectionData: any,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(
        `/api/cms/page-configs/${pageKey}/sections/${sectionId}`,
        sectionData,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deletePageSection(
    pageKey: string,
    sectionId: string,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(
        `/api/cms/page-configs/${pageKey}/sections/${sectionId}`,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async reorderPageSections(
    pageKey: string,
    sectionIds: string[],
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(
        `/api/cms/page-configs/${pageKey}/sections/reorder`,
        { sectionIds },
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Notification APIs
  async getNotifications(
    filters: any = {},
  ): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/notifications", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getAdminNotifications(
    filters: any = {},
  ): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/admin-notifications", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getUnreadNotificationCount(): Promise<ApiResponse<{ count: number }>> {
    try {
      const response = await api.get("/api/admin-notifications/unread-count");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async markNotificationRead(id: string | number): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/api/admin-notifications/${id}/read`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async markAllNotificationsRead(): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/admin-notifications/mark-all-read");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async sendNotification(notificationData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(
        "/api/notifications/send",
        notificationData,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateNotification(
    id: string,
    notificationData: any,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(
        `/api/notifications/${id}`,
        notificationData,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteNotification(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/notifications/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getTemplates(
    filters: any = {},
  ): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/notifications/templates", {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getUserSegments(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/notifications/segments");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getNotificationStats(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/notifications/stats");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getNotificationById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/notifications/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Brevo Sync APIs
  async getBrevoSyncStatus(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/brevo/sync/status");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async syncAllUsersToBrevo(): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/brevo/sync/all");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async syncIncrementalUsersToBrevo(): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/brevo/sync/incremental");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async syncUserToBrevo(userId: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/api/brevo/sync/user/${userId}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getBrevoSyncLogs(
    limit?: number,
    page?: number,
  ): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const params: any = {};
      if (limit) params.limit = limit;
      if (page) params.page = page;

      const response = await api.get("/api/brevo/sync/logs", {
        params,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getBrevoFailureDetails(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/brevo/sync/failure-details");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Booking APIs
  async updateBookingStatus(
    id: string,
    status: string,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/bookings/${id}/status`, { status });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async cancelBooking(
    id: string,
    reason?: string,
    cancellationCategory?: "my_cinemas_issue" | "customer_initiated",
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/api/bookings/${id}/cancel`, {
        reason,
        cancellationCategory,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async refundBooking(
    id: string,
    cancellationReason: string,
    cancellationCategory?: "my_cinemas_issue" | "customer_initiated",
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/api/bookings/${id}/refund`, {
        cancellationReason,
        cancellationCategory,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async refundBookingsBulk(
    bookingIds: string[],
    cancellationReason: string,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/bookings/refund/bulk", {
        bookingIds,
        cancellationReason,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async cancelShow(
    showId: string,
    cinemaId: string,
    showDateTime: string,
    cancellationReason: string,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/shows/cancel", {
        showId,
        cinemaId,
        showDateTime,
        cancellationReason,
      });

      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async resendBookingNotifications(
    id: string,
    channels: string[],
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(
        `/api/bookings/${id}/notifications/resend`,
        { channels },
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async resendBookingEmail(bookingId: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(
        `/api/bookings/${bookingId}/resend-email`,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Settings APIs
  async getSettings(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/settings");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateSettings(settings: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.put("/api/settings", settings);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Booking Fee Tiers APIs
  async getBookingFeeTiers(
    activeOnly: boolean = false,
  ): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get("/api/booking-fee-tiers", {
        params: { active_only: activeOnly },
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getBookingFeeTierById(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/booking-fee-tiers/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createBookingFeeTier(tierData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/booking-fee-tiers", tierData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateBookingFeeTier(
    id: number,
    tierData: any,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/booking-fee-tiers/${id}`, tierData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteBookingFeeTier(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/booking-fee-tiers/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async calculateBookingFee(
    ticketPrice: number,
  ): Promise<ApiResponse<{ booking_fee: number }>> {
    try {
      const response = await api.post("/api/booking-fee-tiers/calculate", {
        ticket_price: ticketPrice,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Admin Users APIs
  async getAdminUsers(
    filters: any = {},
  ): Promise<ApiResponse<{ users: any[]; pagination: any }>> {
    try {
      const response = await api.get("/api/admin-users", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getAdminUserById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/admin-users/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createAdminUser(userData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/admin-users", userData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateAdminUser(id: string, userData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/admin-users/${id}`, userData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteAdminUser(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/admin/users/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getAdminUserRoles(): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get("/api/admin-users/roles");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getAdminUserPermissions(): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get("/api/admin-users/permissions");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Coupons APIs
  async getCoupons(filters: any = {}): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get("/api/coupons", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getCouponById(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/coupons/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getCouponByCode(code: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/coupons/code/${code}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createCoupon(couponData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/coupons", couponData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateCoupon(id: number, couponData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/coupons/${id}`, couponData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteCoupon(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/coupons/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getCouponUsage(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/coupons/${id}/usage`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async exportBookings(filters: any = {}): Promise<void> {
    try {
      const response = await api.get("/api/bookings/export", {
        params: filters,
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let fileName = `bookings_export_${Date.now()}.csv`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Contact / Feedback APIs
  async getContactQueries(
    filters: any = {},
  ): Promise<ApiResponseWithPagination<any[]>> {
    try {
      const response = await api.get("/api/contact/queries", {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getContactQueryById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/contact/queries/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateContactStatus(
    id: string,
    status: string,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/contact/queries/${id}/status`, {
        status,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Beverages Management APIs
  async getBeverages(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/admin/beverages");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createBeverage(beverageData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/admin/beverages", beverageData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateBeverage(
    id: number,
    beverageData: any,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/admin/beverages/${id}`, beverageData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteBeverage(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/admin/beverages/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async toggleBeverageStatus(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.patch(`/admin/beverages/${id}/toggle`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // OFFERS MANAGEMENT APIs
  async getOffers(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("api/offers");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createOffer(offerData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/offers", offerData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateOffer(id: string, offerData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/offers/${id}`, offerData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteOffer(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/offers/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // CITIES MANAGEMENT APIs (admin manages which cities appear in the website city picker)
  async getCities(filters: any = {}): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get("/api/cities", { params: filters });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getCityById(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/api/cities/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async createCity(cityData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/cities", cityData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateCity(id: string, cityData: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.put(`/api/cities/${id}`, cityData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteCity(id: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete(`/api/cities/${id}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Upload Offer Image
  async uploadOfferImage(formData: FormData): Promise<ApiResponse<any>> {
    try {
      const response = await api.post("/api/upload/offer-image", formData, {
        headers: {
          "Content-Type": undefined,
        },
        timeout: 30000,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Upload Document (PDF, DOC, DOCX)
  async uploadDocument(file: File): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/api/upload/document", formData, {
        headers: {
          "Content-Type": undefined,
        },
        timeout: 60000,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Health check
  async healthCheck(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/health");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Error handler
  private handleError(error: any): Error {
    if (error.response?.data) {
      return new Error(
        error.response.data.error ||
          error.response.data.message ||
          "Server error",
      );
    } else if (error.request) {
      return new Error("Network error - unable to connect to server");
    } else {
      return new Error(error.message || "An unexpected error occurred");
    }
  }

  async testZeptoConnection(): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/api/settings/zepto/test-connection`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async sendTestZeptoEmail(email: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/api/settings/zepto/send-test-email`, {
        email,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // ===================================================================
  // Manual Refund (MR) workflow
  // ===================================================================
  async manualRefund(
    bookingId: string,
    payload: {
      mrAmount: number;
      mrQty: number;
      refundMode: "Cash" | "UPI" | "Card";
      refundReason: string;
      referenceNumber?: string;
    },
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(
        `/api/bookings/${bookingId}/manual-refund`,
        payload,
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // ===================================================================
  // 4 BMS-aligned Reports
  // ===================================================================
  async getBookingsReport(filters: {
    dateFrom?: string;
    dateTo?: string;
    cinemaId?: string;
    movieId?: string;
    status?: string;
    paymentMethod?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/reports/bookings", {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async exportBookingsReport(filters: any = {}): Promise<void> {
    try {
      const response = await api.get("/api/reports/bookings/export", {
        params: filters,
        responseType: "blob",
      });
      downloadBlob(response.data, `bookings_report_${Date.now()}.xlsx`);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getCancellationsReport(filters: {
    dateFrom?: string;
    dateTo?: string;
    cinemaId?: string;
    movieId?: string;
    cancelledBy?: string;
    category?: string;
    paymentMethod?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/reports/cancellations", {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async exportCancellationsReport(filters: any = {}): Promise<void> {
    try {
      const response = await api.get("/api/reports/cancellations/export", {
        params: filters,
        responseType: "blob",
      });
      downloadBlob(
        response.data,
        `cancellations_report_${Date.now()}.xlsx`,
      );
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getManualRefundsReport(filters: {
    dateFrom?: string;
    dateTo?: string;
    cinemaId?: string;
    refundMode?: string;
    initiatedBy?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/reports/manual-refunds", {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async exportManualRefundsReport(filters: any = {}): Promise<void> {
    try {
      const response = await api.get("/api/reports/manual-refunds/export", {
        params: filters,
        responseType: "blob",
      });
      downloadBlob(
        response.data,
        `manual_refunds_report_${Date.now()}.xlsx`,
      );
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async getPayoutSummary(filters: {
    dateFrom?: string;
    dateTo?: string;
    cinemaId?: string;
  } = {}): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/reports/payout-summary", {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async exportPayoutSummary(filters: any = {}): Promise<void> {
    try {
      const response = await api.get("/api/reports/payout-summary/export", {
        params: filters,
        responseType: "blob",
      });
      downloadBlob(
        response.data,
        `payout_summary_${Date.now()}.xlsx`,
      );
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // ===================================================================
  // Daily cron-generated payout files (downloadable archive)
  // ===================================================================
  async getDailyPayoutFiles(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get("/api/reports/payout-summary/files");
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async downloadDailyPayoutFile(date: string): Promise<void> {
    try {
      const response = await api.get(
        `/api/reports/payout-summary/file/${date}`,
        { responseType: "blob" },
      );
      downloadBlob(response.data, `payout-summary-${date}.xlsx`);
    } catch (error: any) {
      throw this.handleError(error);
    }
  }
}

// Helper to trigger an Excel download from a Blob response.
function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export const adminService = new AdminService();
export default adminService;
